import { DownOutlined } from '@ant-design/icons';
import {
  IssueCaseDoc,
  IssueCaseStatusCode,
  IssueTaskDoc,
  IssueTaskStatus,
  issueTaskCategoryMap,
} from '@gooduncles/gu-app-schema';
import { Button, Dropdown, message, notification } from 'antd';
import confirm from 'antd/es/modal/confirm';
import { FC } from 'react';

import { errorObjectToString } from 'src/utils/common';
import { formatDate } from 'src/utils/date-util';
import { batchEnd, batchStart, updateIssueCase, updateIssueTask } from 'src/utils/firebase/firebase-short-cut';
import { validateCanceledStatusForIssueTask } from 'src/utils/issue-task-util';
import { issueCaseStatusBadgeMap, validateIssueCaseStatusForChange } from 'src/utils/issue/issue-util';

import { IssueStatusBadge } from '../IssueStatusBadge';

interface IssueCaseStatusSelectorProps {
  issueCaseDoc: IssueCaseDoc;
  issueTasks: IssueTaskDoc[];
  disabled: boolean;
}

const IssueCaseStatusSelector: FC<IssueCaseStatusSelectorProps> = ({ issueCaseDoc, issueTasks, disabled }) => {
  const items = Object.entries(issueCaseStatusBadgeMap)
    .filter(([key]) => key !== IssueCaseStatusCode.DELETED.toString() && key !== IssueCaseStatusCode.PENDING.toString())
    .map(([key, value]) => {
      const isValid = validateIssueCaseStatusForChange(issueCaseDoc.status, Number(key)).isValid;
      if (disabled && key === IssueCaseStatusCode.CANCELED.toString()) {
        return {
          key,
          label: <IssueStatusBadge text={value.text} status={value.status} style={{ opacity: 0.3 }} />,
          disabled: true,
        };
      }
      return {
        key,
        label: <IssueStatusBadge text={value.text} status={value.status} style={!isValid ? { opacity: 0.3 } : {}} />,
        disabled: !isValid,
      };
    });

  const onUpdateIssueCaseStatus = async (status0: IssueCaseStatusCode) => {
    const status = Number(status0);
    const isValid = validateIssueCaseStatusForChange(issueCaseDoc.status, status).isValid;
    if (!isValid) {
      notification.error({
        message: '이슈 상태 변경에 실패했습니다.',
        description: validateIssueCaseStatusForChange(issueCaseDoc.status, status).errorMessage,
      });
      return;
    }

    if ([IssueCaseStatusCode.IN_PROGRESS, IssueCaseStatusCode.CLOSED].includes(status) && issueTasks.length === 0) {
      notification.error({
        message: '태스크가 없습니다.',
      });
      return;
    }

    if (status === IssueCaseStatusCode.CLOSED && issueTasks.some((task) => task.status < IssueTaskStatus.FAILED)) {
      const needToUpdateTasks: IssueTaskDoc[] = issueTasks.filter((task) => task.status < IssueTaskStatus.FAILED);

      // targetDate가 없는 태스크가 있는지 확인
      const tasksWithoutTargetDate = needToUpdateTasks.filter((task) => !task.targetDate);
      if (tasksWithoutTargetDate.length > 0) {
        const taskMessages = tasksWithoutTargetDate
          .map((task, index) => `${index + 1}. ${issueTaskCategoryMap[task.category]}`)
          .join('\n');

        notification.error({
          message: '처리일이 지정되지 않은 태스크가 있습니다.',
          description: `다음 태스크들의 처리일을 먼저 지정해주세요:\n\n${taskMessages}`,
          duration: 10,
          style: { whiteSpace: 'pre-line' },
        });
        return;
      }

      confirm({
        title: '완료되지 않은 태스크가 있습니다.',
        content: '태스크도 함께 완료하시겠습니까?',
        okText: '일괄 완료',
        cancelText: '아니오',
        okCancel: true,
        onOk: async () => {
          try {
            batchStart();
            const finishedAt = formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss+0900");
            for (const task of needToUpdateTasks) {
              if (task.status < IssueTaskStatus.FAILED) {
                await updateIssueTask(task._id, { status: IssueTaskStatus.COMPLETED, finishedAt }, { bBatch: true });
              }
            }
            await updateIssueCase(issueCaseDoc._id, { status: status }, { bBatch: true });
            await batchEnd();
            message.success(`${needToUpdateTasks.length}건의 태스크를 함께 완료 처리했습니다.`);
          } catch (error) {
            console.error(error);
            notification.error({
              message: '케이스 상태 변경에 실패했습니다.',
              description: errorObjectToString(error),
            });
          }
        },
      });
    } else if (
      status === IssueCaseStatusCode.CANCELED &&
      issueTasks.some((task) => task.status < IssueTaskStatus.CANCELED)
    ) {
      // 취소하려는 태스크들에 대해 먼저 검증
      const needToUpdateTasks: IssueTaskDoc[] = issueTasks.filter((task) => task.status < IssueTaskStatus.CANCELED);

      // 모든 태스크에 대해 취소 가능 여부 검증
      const validationResults = await Promise.all(
        needToUpdateTasks.map(async (task) => ({
          task,
          validation: await validateCanceledStatusForIssueTask(task),
        })),
      );

      const failedTasks = validationResults.filter((result) => !result.validation.isValid);

      if (failedTasks.length > 0) {
        // 검증 실패한 태스크가 있으면 에러 표시
        const failedTaskMessages = failedTasks
          .map(
            (result, index) =>
              `${index + 1}. ${issueTaskCategoryMap[result.task.category]}\n   사유: ${result.validation.errorMessage}`,
          )
          .join('\n\n');

        notification.error({
          message: '일부 태스크를 취소할 수 없습니다.',
          description: `다음 태스크들의 조건을 먼저 충족해야 합니다:\n\n${failedTaskMessages}`,
          duration: 10,
          style: { whiteSpace: 'pre-line' },
        });
        return;
      }

      // 모든 검증 통과 시 confirm 창 표시
      confirm({
        title: '취소되지 않은 태스크가 있습니다.',
        content: '태스크도 함께 취소하시겠습니까?',
        okText: '일괄 취소',
        cancelText: '아니오',
        okCancel: true,
        onOk: async () => {
          try {
            batchStart();
            for (const task of needToUpdateTasks) {
              await updateIssueTask(task._id, { status: IssueTaskStatus.CANCELED }, { bBatch: true });
            }
            await updateIssueCase(issueCaseDoc._id, { status: status }, { bBatch: true });
            await batchEnd();
            message.success(`${needToUpdateTasks.length}건의 태스크를 함께 취소 처리했습니다.`);
          } catch (error) {
            console.error(error);
            notification.error({
              message: '케이스 상태 변경에 실패했습니다.',
              description: errorObjectToString(error),
            });
          }
        },
      });
    } else {
      try {
        await updateIssueCase(issueCaseDoc._id, { status: status });
      } catch (error) {
        console.error(error);
        notification.error({
          message: '케이스 상태 변경에 실패했습니다.',
          description: errorObjectToString(error),
        });
      }
    }
  };

  return (
    <Dropdown
      menu={{
        items,
        selectable: true,
        selectedKeys: [issueCaseDoc.status.toString()],
        onSelect: (event) => onUpdateIssueCaseStatus(event.key as unknown as IssueCaseStatusCode),
      }}
      trigger={['click']}
    >
      <Button type='text' size='small' icon={<DownOutlined />} iconPosition='end'>
        <IssueStatusBadge
          text={issueCaseStatusBadgeMap[issueCaseDoc.status].text}
          status={issueCaseStatusBadgeMap[issueCaseDoc.status].status}
        />
      </Button>
    </Dropdown>
  );
};

export default IssueCaseStatusSelector;
